import DimensionUtils from "../common/DimensionUtils";
import { resolveUnitConfig, unitNamesOf } from "../common/inputs/unitConfig";
import { ALL_EDGE_KEYS, SIDE_KEYS, VISIBLE_EDGE_KEY, ZERO_GUID } from "../configurator/cards/edgeKeys";
import IBoardData from "../database/interfaces/IBoardData";
import IUnitsTypeDescriptor from "../database/interfaces/paramScript/IUnitsTypeDescriptor";
import IParamTypeDescriptor, {
    ICountParamTypeDescriptor,
    IDecorParamTypeDescriptor,
    IEnumParamTypeDescriptor,
    IRailingParamTypeDescriptor,
    IRelationFieldOverride,
    ISizeParamTypeDescriptor,
} from "../database/interfaces/paramScript/IParamTypeDescriptor";
import { guardDimension } from "./paramGuards";

/**
 * PARAMÉTER-ALAPÉRTELMEZÉSEK — "mi ennek a mezőnek az ÁLTALÁNOS (nem felhasználó-specifikus) értéke?"
 *
 * MIÉRT KÜLÖN MODUL: az eltárolt érték (IParamScript.parameterValue) a cart > customer > shop > global
 * feloldás EREDMÉNYE, tehát bele van keverve a felhasználó saját preferenciája is (EA-485). Az
 * alapértelmezés ezzel szemben KÉT, egyaránt shop-szintű forrásból jöhet — a SORREND itt lényeges:
 *
 *   1) ELSŐDLEGES: a DEFAULT érték-sor (IParamScript.defaultValue). Default az a sor, ami SEMMILYEN
 *      customerhez és cart-hoz nem tartozik; a szerver az AKTUÁLIS BOLT ilyen sorát adja, és ha olyan
 *      nincs, a bolt-független (id_shop NULL) sort — lásd ParamsResource `spdef_shop` / `spdef_global`.
 *      EZ a tényleges gyári érték: pontosan ezt kapja egy olyan látogató, akinek még nincs saját
 *      preferenciája, tehát a "visszaállítás" is ezt jelenti. A sorrend szándékosan ugyanaz, amit a
 *      betöltött érték feloldása (`filterParams`) használ — különben a reset máshova állna vissza,
 *      mint amit a látogató induláskor kapott. Paraméterenként karbantartott adat.
 *   2) FALLBACK: a TYPE DESCRIPTOR deklarált defaultja (Size `units.<egység>.default`, Count
 *      `config.default`, Enum `default`, …). Ez a mező STRUKTURÁLIS alapértéke — az, amit a kártya
 *      üres/olvashatatlan értéknél amúgy is mutatna. A gyakorlatban sablon-szerű (magasság→1200,
 *      szélesség→600 mindenhol), ezért csak ott dönt, ahol nincs globális sor.
 *
 * Egyik forrás sem felhasználó-specifikus, tehát a reset MINDIG a "gyári" állapotot adja vissza.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * A MINTA (miért ennyi és nem több függvény)
 *
 * A descriptor-nyelvben összesen HÁROM levél-mezőfajta létezik, és ez a paraméter-típusok számától
 * FÜGGETLEN — ugyanaz a három tér vissza a Size-ban, a Railing szélességében, a BackPassing
 * ráhagyásában és minden jövőbeli kártyában:
 *   - méret     → `units` blokk (min/max/lépések/tizedesek/egységek),
 *   - választós → `options` map (+ deklarált default, + a nem választható "ki" érték),
 *   - szám      → `{min, max, default}` hármas.
 * Ezért EGYETLEN feloldó van rájuk (`fieldDefault`), amit a mező LEÍRÓJA (TFieldSpec) vezérel, nem a
 * paraméter típusa. Egy összetett (composite) paraméter pedig nem új logika, csak nevesített levelek
 * listája — azt a `compositeDefault` fésüli össze.
 *
 * Így egy paraméter-típus bekötése ADAT, nem kód: a PARAM_DEFAULTS regiszterben egyetlen sor mondja
 * meg, a descriptor MELYIK része írja le a mezőt. Egy új szám- vagy választós kártya-típus tehát egy
 * sor; egy új composite néhány sor; ÚJ FÜGGVÉNY csak akkor kell, ha tényleg új levél-fajta születik a
 * descriptor-nyelvben (akkor viszont egyetlen ág a `fieldDefault`-ban, minden hívó automatikusan kapja).
 *
 * A Decor SZÁNDÉKOSAN kivétel: ott az alapérték nem "kiválasztás", hanem PATCH egy meglévő objektumra,
 * adat-LÉTEZÉSRE épülő szabályokkal (élzárhatóság, eltűnt lap) — ugyanaz a megkülönböztetés, amiért a
 * paramGuards.ts-ből is kimarad. Egy DSL-be erőltetve több lenne a bonyolultság, nem kevesebb.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * A globális érték ugyanazon a GUARDon megy át, mint betöltéskor (paramGuards): egy utólag szigorított
 * descriptor-korlát (min/max, egység-lista, opció-lista) alá eső régi alapérték így nem tud a
 * reset-gombon keresztül visszaszivárogni — a látszó mező, a Unity-kimenet és a kosár nem csúszhat el.
 *
 * MIT NEM CSINÁL: nem ír state-et, nem validál, nem indít autoedge-et, nem nyúl az effects/visibility
 * logikához. Tiszta függvények — a tényleges írás a hívó dolga, a kártyák meglévő útján (tehát a
 * preferencia-mentés és a Unity-kör is a megszokott módon fut).
 */

/** Egy mező alapértelmezett értéke: a beírandó (tárolási formájú) érték + emberi címke. */
export type TParamDefault = {
    value: any;
    label: string;
};

/**
 * EGY MEZŐ LEÍRÓJA — a descriptor azon (kivágott) része, ami az alapértéket meghatározza. A jelenlévő
 * kulcs dönti el, melyik levél-fajtáról van szó; a `fieldDefault` erre ágazik. Új levél-fajta = új tag
 * ebben az unióban + egy ág a `fieldDefault`-ban, semmi más.
 */
export type TFieldSpec =
    /** Méret-mező: a `units` blokk + opcionális kötés-függő felülírás (BackPassing `byRelation`). */
    | { units: IUnitsTypeDescriptor | undefined; override?: IRelationFieldOverride }
    /** Választós mező: opció-lista, deklarált default, és a nem választható "ki" érték (Railing). */
    | { options: { [name: string]: string } | undefined; default?: string; offValue?: string }
    /** Egyszerű szám-mező: a deklarált default a saját korlátai közé szorítva (Count). */
    | { number: { default?: number; min?: number; max?: number } | undefined }
    /**
     * Logikai mező: be/ki kapcsoló (composite al-mezők, pl. a CabinetTilt osztólapjai). A TÁROLT alak
     * "true"/"false" STRING — ugyanaz, amit a BackPassing `Top`-ja és a DB-ben álló érték is használ.
     */
    | { bool: { default?: boolean } | undefined };

/** Egy composite paraméter NEVESÍTETT levele: melyik kulcs alá kerül, mi írja le, mi a gyári értéke. */
export type TCompositeField = { key: string; spec: TFieldSpec; stored?: any };

/** A feloldáshoz szükséges bemenet. A hívó (PropertyContainerCard) mindig ugyanezt az objektumot adja. */
export type TParamDefaultInput = {
    descriptor: any;
    storedDefault?: any;
    currentValue?: any;
    findBoard?: (id: string) => IBoardData | null;
};

/**
 * A kikapcsolt állapot felirata a tooltipben. Olyan választós mezőkhöz kell, ahol az alapértelmezett
 * érték szándékosan NEM választható opció (Railing `offValue` = a kikapcsolt checkbox implicit értéke),
 * így nincs hozzá címke a descriptorban. UI-szöveg, nem üzleti adat.
 */
const OFF_LABEL = "kikapcsolva";

/** Logikai mező be/ki állapotának felirata a tooltipben. UI-szöveg, nem üzleti adat. */
const BOOL_LABELS = { on: "bekapcsolva", off: "kikapcsolva" };

/** A globális alapérték szöveges alakja, ha a szerver adott ilyet (üres/hiányzó → undefined). */
const storedText = (storedDefault: any): string | undefined =>
    (storedDefault === undefined || storedDefault === null || String(storedDefault) === "")
        ? undefined
        : String(storedDefault);

/** Nem definiált korlát sosem harap (ugyanaz a szabály, mint a paramGuards clampjénél). */
const clamp = (num: number, min?: number, max?: number): number =>
    Math.min(Math.max(num, min ?? num), max ?? num);

/**
 * EGY MEZŐ alapértéke — a modul EGYETLEN levél-feloldója, minden paraméter-típusnak és a composite
 * kártyák al-mezőinek is (BackPassing kötés / ráhagyás / mélység).
 *
 * A sorrend mindhárom ágban azonos: GLOBÁLIS alapérték (a mező saját korlátaihoz igazítva) →
 * a descriptor deklarált defaultja. A méret-ág ezt külön elágazás nélkül éri el: az ÜRES stringgel
 * hívott `resolveUnitConfig` magától a descriptor strukturális alapértékére esik vissza.
 */
export const fieldDefault = (spec: TFieldSpec, storedDefault?: any): TParamDefault | null => {
    const stored = storedText(storedDefault);

    // Méret: a globális értéket ugyanaz a guard igazítja, ami betöltéskor is fut. A felirat
    // `valueLabel`-es egységnél a descriptorból jön (pl. munkalap "auto" → "automatikus").
    if ("units" in spec) {
        const { units, override } = spec;
        if (!unitNamesOf(units).length) return null;

        const guarded = stored !== undefined ? String(guardDimension(units, stored, override).value) : "";
        const cfg = resolveUnitConfig(units, guarded);
        // A kötés-függő korlát a DESCRIPTOR-fallbackra is vonatkozik (a guard csak az eltárolt értéket
        // szorítja be) — így egy sablon-default sem eshet a kötéshez tartozó korlátokon kívülre.
        const num = clamp(cfg.num, override?.min ?? cfg.min, override?.max ?? cfg.max);

        return {
            value: DimensionUtils.formatDimension(num, cfg.unit),
            label: cfg.valueLabel ?? `${num.toFixed(override?.decimals ?? cfg.decimals)} ${cfg.unit}`.trim(),
        };
    }

    // Választós: csak LÉTEZŐ opció (vagy az `offValue`) lehet alapérték; a címke az opció EMBERI neve.
    if ("options" in spec) {
        const map = spec.options ?? {};
        if (!Object.keys(map).length) return null;

        const isValid = (candidate: any): candidate is string =>
            typeof candidate === "string" && candidate !== "" && (candidate in map || candidate === spec.offValue);

        const value = [stored, spec.default].find(isValid);
        if (value === undefined) return null;

        return { value, label: map[value] ?? (value === spec.offValue ? OFF_LABEL : value) };
    }

    // Logikai: a globális alapérték, egyébként a descriptor deklarált `default`-ja. Ha egyik sem
    // mondja meg, NINCS alapérték (nem találunk ki false-ot — az üzleti döntés lenne a kódban).
    if ("bool" in spec) {
        const declared = spec.bool?.default;
        const source = stored ?? (declared === undefined ? undefined : String(declared));
        if (source === undefined) return null;

        const on = String(source) === "true";
        return { value: String(on), label: on ? BOOL_LABELS.on : BOOL_LABELS.off };
    }

    // Szám: ha egyik forrás sem ad értelmes számot, NINCS mihez visszaállítani — ilyenkor a gomb ki sem
    // kerül, nem pedig kitalálunk egy értéket (pl. a `min`-t), ami üzleti döntés lenne a kódban.
    const num = clamp(parseFloat(stored ?? String(spec.number?.default ?? "")), spec.number?.min, spec.number?.max);
    if (isNaN(num)) return null;

    return { value: String(num), label: String(num) };
};

/**
 * Egy COMPOSITE paraméter alapértéke: nevesített levelek összefésülése egyetlen objektum-értékké és
 * egy olvasható feliratba. A fel nem oldható levelek KIMARADNAK (nem rontják el a többit); ha egyik sem
 * oldható fel, nincs alapérték. Így a kiadott érték alakja pontosan az, amit a kártya is emitálna: ami
 * a descriptorban nincs (pl. nem migrált környezetben a railing szélessége), az a kimenetből is hiányzik.
 */
export const compositeDefault = (fields: TCompositeField[]): TParamDefault | null => {
    const value: Record<string, any> = {};
    const labels: string[] = [];

    fields.forEach((field) => {
        const resolved = fieldDefault(field.spec, field.stored);
        if (!resolved) return;
        value[field.key] = resolved.value;
        labels.push(resolved.label);
    });

    return labels.length ? { value, label: labels.join(", ") } : null;
};

/**
 * Decor: a descriptor NEM deklarál alapértelmezett lapot/élt (nem is szabadna: az termék-adat), ezért
 * itt a GLOBÁLIS érték-sor az egyetlen forrás (`storedDefault`).
 *
 * A visszaállítás PATCH, nem csere. A tárolt alapérték a TÖMÖR alakot használja ({BoardProduct,
 * EdgeProduct}), a state-ben viszont a betöltéskor kibontott alak él (oldalankénti élek, mintázat-irány,
 * láthatóság-élek). Ha a tömör alakot nyersen írnánk vissza, a Unity felé menő JSON SÉMÁJA változna meg.
 * Ezért a mostani (már normalizált) értékre tesszük rá, amit az alapérték deklarál:
 *   - a lap (`BoardProduct`),
 *   - az élek: a tömör `EdgeProduct` a NÉGY oldal élét jelenti (ugyanaz a kibontás, amit a betöltés is
 *     végez); a már kifejtett él-mezőket értelemszerűen közvetlenül vesszük át,
 *   - nem élzárható alap-lapnál MINDEN él-mező kikerül — ugyanaz a szabály, mint betöltéskor,
 *   - a duplung leválik: a gyári alapérték nem ismer duplungot, és egy egyrétegű lapra méretezett gyári
 *     él nem maradhat dupla vastagságú lapon.
 * Minden más mező (pl. mintázat-irány) érintetlen marad.
 */
const decorDefault = ({ descriptor, storedDefault, currentValue, findBoard }: TParamDefaultInput): TParamDefault | null => {
    const d = descriptor as IDecorParamTypeDescriptor;
    // A dekor szerkeszthetőségét a kártyával AZONOS feltétel dönti el (lásd BoardDecorCardContent):
    // a param és a board-blokk is engedélyezett kell legyen.
    if (d?.board?.editable !== true || d?.board?.readOnly === true) return null;

    const boardId = storedDefault?.BoardProduct;
    if (!boardId) return null;

    // Eltűnt/ismeretlen alap-lap: nincs mit visszaállítani (és nevet sem tudnánk kiírni) → nincs gomb.
    const board = findBoard?.(boardId);
    if (!board) return null;

    const value: Record<string, any> = (currentValue && typeof currentValue === "object")
        ? { ...currentValue }
        : {};
    value.BoardProduct = boardId;

    delete value.DuplungProduct;
    delete value.DuplungPatternDirection;
    delete value.DuplungFixing;

    if (board.EdgeBandable != 1) {
        ALL_EDGE_KEYS.forEach((key) => delete value[key]);
    } else {
        if (storedDefault.EdgeProduct) {
            // A reset a GYÁRI állapotot adja vissza, nem a mostanit foltozza: MINDEN él-mező kiürül,
            // és a gyári él EGYETLEN célra kerül. Így a kártyán egy lap + egy él-bejegyzés marad —
            // pontosan az, amit egy mentett preferencia nélküli ELSŐ betöltés ad.
            //
            // A cél a LÁTHATÓ él ott, ahol a kártya kirakja a láthatóság-gombokat; ahol nem
            // (front, hátfal, munkalap, lábazat), ott a négy oldal — különben olyan tokent kapna a
            // bejegyzés, amihez nincs is gomb.
            ALL_EDGE_KEYS.forEach((key) => delete value[key]);

            const targets: readonly string[] = d?.edge?.visibilityLabels ? [VISIBLE_EDGE_KEY] : SIDE_KEYS;
            targets.forEach((key) => { value[key] = storedDefault.EdgeProduct; });
        }
        ALL_EDGE_KEYS.forEach((key) => {
            if (storedDefault[key] !== undefined) value[key] = storedDefault[key];
        });
    }

    return { value, label: board.Name };
};

/**
 * PARAMÉTER-TÍPUS → a descriptor MELYIK része írja le a mezőt. A sorok szándékosan egyszerű
 * "kivágások": a feloldás logikája a `fieldDefault` / `compositeDefault` közös kódjában van.
 *
 * A BackPassing SZÁNDÉKOSAN hiányzik: annak a kártyája MEZŐNKÉNT rak ki reset-gombot (kötés, ráhagyás,
 * mélység), közvetlenül `fieldDefault`-tal — egy kártya-szintű gomb ott csak duplikálná őket.
 */
const PARAM_DEFAULTS: Record<string, (input: TParamDefaultInput) => TParamDefault | null> = {
    Size: ({ descriptor, storedDefault }) =>
        fieldDefault({ units: (descriptor as ISizeParamTypeDescriptor)?.units }, storedDefault),

    Count: ({ descriptor, storedDefault }) =>
        fieldDefault({ number: (descriptor as ICountParamTypeDescriptor)?.config }, storedDefault),

    // Az Enum-kártya SZIGORÚBB az általánosnál: nála a hiányzó `editable` letiltást jelent
    // (EnumCardContent: `editable ?? false`). A gomb pontosan akkor jelenjen meg, amikor a select is él.
    Enum: ({ descriptor, storedDefault }) => {
        const d = descriptor as IEnumParamTypeDescriptor;
        return d?.editable !== true ? null : fieldDefault(
            { options: d.options, default: d.default !== undefined ? String(d.default) : undefined },
            storedDefault
        );
    },

    // Railing {RailingType, RailingWidth}: EGY gomb az egész composite-hoz (a be/ki checkbox állapota is
    // a típusból következik, ezért külön nem lenne értelme). Az `offValue` érvényes alapérték, de
    // szándékosan nem választható opció — lásd IRailingParamTypeDescriptor.
    Railing: ({ descriptor, storedDefault }) => {
        const d = descriptor as IRailingParamTypeDescriptor;
        const stored = (storedDefault && typeof storedDefault === "object") ? storedDefault : {};

        const fields: TCompositeField[] = [{
            key: "RailingType",
            stored: stored.RailingType,
            spec: {
                options: d?.railingType?.options,
                default: d?.railingType?.default,
                offValue: d?.railingType?.offValue,
            },
        }];
        if (d?.railingWidth) {
            fields.push({
                key: "RailingWidth",
                stored: stored.RailingWidth ?? d.railingWidth.default,
                spec: { units: d.railingWidth.units },
            });
        }
        return compositeDefault(fields);
    },

    Decor: decorDefault,
};

/**
 * Egy EGÉSZ paraméter alapértelmezett értéke a típusa + descriptora (+ a globális alapérték) alapján.
 * Ismeretlen típusnál, descriptor nélkül, nem szerkeszthető mezőnél, vagy ha egyik forrás sem ad
 * alapértelmezést: `null` (a hívó ilyenkor nem rak ki reset-gombot).
 */
export const resolveParamDefault = (
    parameterType: string | null,
    descriptor: IParamTypeDescriptor | undefined,
    input?: Omit<TParamDefaultInput, "descriptor">
): TParamDefault | null => {
    const resolve = parameterType ? PARAM_DEFAULTS[parameterType] : undefined;
    if (!resolve || !descriptor) return null;

    // Amit a felhasználó nem is szerkeszthet, azt nem is állíthatja vissza — ugyanazok a descriptor-
    // mezők döntenek, mint a kártyáknál (`editable` hiányában engedékeny, `readOnly` hiányában nem az).
    if (descriptor.editable === false || descriptor.readOnly === true) return null;

    return resolve({ ...input, descriptor });
};

/** Összehasonlítható alak: az objektum-értékek (Decor, Railing) is stabilan összevethetők. */
const normalize = (value: any): string =>
    value === undefined || value === null
        ? ""
        : (typeof value === "object" ? JSON.stringify(value) : String(value));

/**
 * A mező MÁR az alapértelmezetten áll-e. Az ÜRES érték is ilyennek számít: ilyenkor a kártya amúgy is
 * az alapértelmezést mutatja (lásd unitConfig), tehát nincs mit visszaállítani.
 */
export const isAtParamDefault = (currentValue: any, defaultValue: any): boolean => {
    const current = normalize(currentValue);
    return current === "" || current === normalize(defaultValue);
};
